import React, { useEffect, useState } from 'react';

import { Button } from '../../ui/Button/Button';
import { CatalogThumb } from '../../ui/CatalogThumb/CatalogThumb';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import { Panel } from '../../ui/Panel/Panel';
import { cx } from '../../../utils/cx';
import type { CatalogEntry, DimensionValues } from '../../../entities/productAnalogs/model/types';
import styles from './ProductDimensionsPanel.module.scss';
import { IconClose, InfoIcon } from '../../../assets/icons/icons';

interface ProductDimensionsPanelProps {
    entry: CatalogEntry;
    busy?: boolean;
    onSave: (entry: CatalogEntry, values: DimensionValues) => Promise<boolean>;
    onClose: () => void;
}

type DimKey = 'length' | 'width' | 'height' | 'weight';

const ROWS: { key: DimKey; label: string }[] = [
    { key: 'length', label: 'Длина, мм' },
    { key: 'width', label: 'Ширина, мм' },
    { key: 'height', label: 'Высота, мм' },
    { key: 'weight', label: 'Вес, г' },
];

const TRANSPORT_KEY: Record<DimKey, keyof DimensionValues> = {
    length: 'transportLength',
    width: 'transportWidth',
    height: 'transportHeight',
    weight: 'transportWeight',
};

type Draft = Record<DimKey, string>;

const EMPTY_DRAFT: Draft = { length: '', width: '', height: '', weight: '' };

const toDraft = (dims: DimensionValues, transport: boolean): Draft => ({
    length: String((transport ? dims.transportLength : dims.length) ?? ''),
    width: String((transport ? dims.transportWidth : dims.width) ?? ''),
    height: String((transport ? dims.transportHeight : dims.height) ?? ''),
    weight: String((transport ? dims.transportWeight : dims.weight) ?? ''),
});

const num = (raw: string): number | null => (raw === '' ? null : Number(raw));

export function ProductDimensionsPanel({ entry, busy, onSave, onClose }: ProductDimensionsPanelProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [tDraft, setTDraft] = useState<Draft>(EMPTY_DRAFT);

    useEffect(() => {
        setEditing(false);
    }, [entry.id]);

    const dims = entry.dimensions;

    const header = (
        <>
            <div className={styles.titleGroup}>
                <h3 className={styles.title}>Габариты товара</h3>
                <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
                    <IconClose />
                </button>
            </div>

            <div className={styles.product}>
                <CatalogThumb src={entry.imageUrl} large />
                <span>
                    <div className={styles.name}>{entry.name}</div>
                    <div className={styles.id}>ID: {entry.number}</div>
                </span>
            </div>
        </>
    );

    if (!dims) {
        return (
            <Panel bordered>
                <div className={styles.panel}>
                    {header}
                    <EmptyState
                        title="Габариты ещё не рассчитаны"
                        description="Расчёт запускается на странице позиции в проекте."
                    />
                </div>
            </Panel>
        );
    }

    const startEdit = () => {
        setDraft(toDraft(dims, false));
        setTDraft(toDraft(dims, true));
        setEditing(true);
    };

    const isChanged = ROWS.some(
        ({ key }) => num(draft[key]) !== dims[key] || num(tDraft[key]) !== dims[TRANSPORT_KEY[key]],
    );

    const save = async () => {
        const ok = await onSave(entry, {
            length: num(draft.length),
            width: num(draft.width),
            height: num(draft.height),
            weight: num(draft.weight),
            transportLength: num(tDraft.length),
            transportWidth: num(tDraft.width),
            transportHeight: num(tDraft.height),
            transportWeight: num(tDraft.weight),
        });
        if (ok) setEditing(false);
    };

    const cell = (key: DimKey, transport: boolean) => {
        const value = editing
            ? transport
                ? tDraft[key]
                : draft[key]
            : String(dims[transport ? TRANSPORT_KEY[key] : key] ?? '—');

        const set = transport ? setTDraft : setDraft;

        return (
            <td>
                <input
                    className={styles.input}
                    inputMode="numeric"
                    readOnly={!editing}
                    aria-label={`${key}, ${transport ? 'транспортный' : 'эталон'}`}
                    value={value}
                    onChange={(event) => set((prev) => ({ ...prev, [key]: event.target.value.replace(/\D/g, '') }))}
                />
            </td>
        );
    };

    return (
        <Panel bordered>
            <div className={styles.panel}>
                {header}

                <div className={styles.badges}>
                    <span className={cx(styles.badge, dims.source === 'reference' ? styles.badgeOk : styles.badgeWarn)}>
                        {dims.source === 'reference' ? 'Находится в разделе «Эталон»' : 'Находится в разделе «Неподтвержденное»'}
                    </span>
                </div>

                <table className={styles.dims}>
                    <thead>
                        <tr>
                            <th />
                            <th>Эталон</th>
                            <th>Трансп.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ROWS.map(({ key, label }) => (
                            <tr key={key}>
                                <th scope="row">{label}</th>
                                {cell(key, false)}
                                {cell(key, true)}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className={styles.actions}>
                    {editing ? (
                        <>
                            <Button variant="primary" disabled={!isChanged || busy} onClick={save}>
                                Сохранить изменения
                            </Button>
                            <Button disabled={busy} onClick={() => setEditing(false)}>
                                Отмена
                            </Button>
                        </>
                    ) : (
                        <Button onClick={startEdit}>Редактировать</Button>
                    )}
                </div>

                <div className={styles.hintGroup}>
                    <button
                        type="button"
                        className={styles.metaBtn}
                    >
                        <InfoIcon />
                    </button>
                    <p className={styles.hint}>
                        Эталонные габариты используются для расчётов и сопоставления товаров, транспортные — для
                        логистики.
                    </p>
                </div>
            </div>
        </Panel>
    );
}